import api from '../../services/api';
import liveChatApi, { centralChatTicketsEnabled } from '../../services/liveChatApi';
import { centralChatPlayerIdentity, sourceChatPlayerIdentity } from '../../services/chatSourceIdentity';

export const CHAT_PLAYER_SEARCH_DEBOUNCE_MS = 250;
export const normalizeChatSearch = (value) => String(value || '').trim().toLowerCase();

// Source profiles are authoritative for new signups, including players who
// have not started a conversation. Chat-room search alone cannot find them.
export async function searchChatPlayerDirectory({ term, tenantId, agentPortal, signal, central = centralChatTicketsEnabled }) {
  const query = String(term || '').trim();
  if (query.length < 2 || !tenantId || ['all', 'none'].includes(tenantId)) return [];
  const sourceTenantId = sourceChatPlayerIdentity('', tenantId).tenantId;
  const chatTenantId = centralChatPlayerIdentity('', tenantId, central).tenantId;
  let players;
  if (!agentPortal) {
    const params = new URLSearchParams({
      // The users endpoint accepts a regex; type-ahead must match literal text.
      search: query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      tenant_id: sourceTenantId,
      players_only: 'true',
      limit: '50',
    });
    try {
      const response = await api.get(`/api/admin/users?${params}`, { signal });
      players = response.data?.users || [];
    } catch (error) {
      // Chat-only staff need not gain users:view. Agents never use this API.
      if (error.response?.status !== 403) throw error;
    }
  }
  if (!players) {
    const params = new URLSearchParams({ q: query, tenant_id: chatTenantId, limit: '50' });
    const response = await liveChatApi.get(`/api/chat/staff/players/search?${params}`, { signal });
    players = response.data?.players || [];
  }
  const seen = new Set();
  return players.map((player) => {
    const identity = centralChatPlayerIdentity(player.id, player.tenant_id, central);
    return { ...player, id: identity.playerId, tenant_id: identity.tenantId };
  }).filter((player) => {
    if (!player?.id || player.tenant_id !== chatTenantId || seen.has(player.id)) return false;
    seen.add(player.id);
    return true;
  });
}

export function unmatchedChatPlayers(players, rooms) {
  const existing = new Set(rooms.map((room) => room.player_id));
  return players.filter((player) => !existing.has(player.id));
}

export async function recentChatSignups(tenantId, { central = centralChatTicketsEnabled, signal } = {}) {
  const sourceTenant = sourceChatPlayerIdentity('', tenantId).tenantId;
  if (sourceTenant !== '661abea4-94a9-4a1e-acc2-b9dd3bb21268') return [];
  const response = await api.get(`/api/admin/users/chat-new?tenant_id=${encodeURIComponent(sourceTenant)}`, { signal });
  return (response.data?.players || []).map((player) => {
    const identity = centralChatPlayerIdentity(player.id, player.tenant_id, central);
    return { ...player, id: identity.playerId, tenant_id: identity.tenantId };
  }).filter((player) => player.id && player.tenant_id === centralChatPlayerIdentity('', sourceTenant, central).tenantId);
}

export function withRecentSignupRooms(rooms = [], signups = []) {
  const byPlayer = new Map(rooms.map((room) => [room.player_id, room]));
  const additions = [];
  const decorated = new Map();
  for (const player of signups) {
    if (!player?.id || !player?.tenant_id) continue;
    const room = byPlayer.get(player.id);
    if (room && room.tenant_id !== player.tenant_id) continue;
    // A real exchange moves the player back into the ordinary chat list.
    if (room && (room.conversation_started_at ||
      (room.last_message && ['player', 'staff'].includes(room.last_sender_role) && room.newly_joined !== true))) continue;
    const notice = `${player.full_name || player.username || 'Player'} joined ${room?.tenant_name || 'Slots Oceans'}`;
    if (room) {
      decorated.set(room.id, { ...room, newly_joined: true,
        newly_joined_at: player.created_at, newly_joined_notice: room.newly_joined_notice || notice });
    } else {
      additions.push({ id: `signup:${player.id}`, player_id: player.id, tenant_id: player.tenant_id,
        player_name: player.full_name || player.username || 'Player', player_username: player.username,
        player_avatar_url: player.avatar_url, tenant_name: 'Slots Oceans',
        newly_joined: true, newly_joined_at: player.created_at,
        newly_joined_notice: notice, last_message_at: player.created_at,
        signup_placeholder: true, source_player: player, staff_unread: 0 });
    }
  }
  return [...rooms.map((room) => decorated.get(room.id) || room), ...additions];
}

export async function openDirectoryPlayerChat(player, tenantId, central = centralChatTicketsEnabled) {
  const identity = centralChatPlayerIdentity(player?.id, player?.tenant_id, central);
  const chatTenantId = centralChatPlayerIdentity('', tenantId, central).tenantId;
  if (!player?.id || !tenantId || identity.tenantId !== chatTenantId) {
    throw new Error('Player is outside the current chat workspace');
  }
  // Searching never creates rooms or messages. This explicit click reuses the
  // existing central conversation and keeps server tenant/agent checks intact.
  const response = await liveChatApi.post('/api/chat/staff/conversations/direct', {
    player_id: identity.playerId,
    tenant_id: chatTenantId,
  });
  const room = response.data?.conversation;
  if (!room?.id || room.player_id !== identity.playerId || room.tenant_id !== chatTenantId) {
    throw new Error('Could not open the player in this chat workspace');
  }
  return room;
}
